import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Deck, DeckWithCardCount } from '../types';
import ConfirmationModal from './ConfirmationModal';
import AIDeckCreator from './AIDeckCreator';
import ExportDecksModal from './ExportDecksModal';
import { getFileService } from '../services/FileServiceFactory';
import { getDatabaseService } from '../services/DatabaseFactory';

const DeckBrowser: React.FC = () => {
  const { state, loadDecksWithCardCounts, deleteDeck, createDeck } = useApp();
  const [searchTerm, setSearchTerm] = useState(() => sessionStorage.getItem('deckBrowser.searchTerm') || '');
  const [difficultyFilter, setDifficultyFilter] = useState(() => sessionStorage.getItem('deckBrowser.difficultyFilter') || '');
  const [languageFilter, setLanguageFilter] = useState(() => sessionStorage.getItem('deckBrowser.languageFilter') || '');

  useEffect(() => { sessionStorage.setItem('deckBrowser.searchTerm', searchTerm); }, [searchTerm]);
  useEffect(() => { sessionStorage.setItem('deckBrowser.difficultyFilter', difficultyFilter); }, [difficultyFilter]);
  useEffect(() => { sessionStorage.setItem('deckBrowser.languageFilter', languageFilter); }, [languageFilter]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deckToDelete, setDeckToDelete] = useState<Deck | null>(null);
  const [showResetProgressModal, setShowResetProgressModal] = useState(false);
  const [deckToResetProgress, setDeckToResetProgress] = useState<Deck | null>(null);
  const [showNewDeckModal, setShowNewDeckModal] = useState(false);
  const [showAIDeckModal, setShowAIDeckModal] = useState(false);
  const [showExportDecksModal, setShowExportDecksModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadDecksWithCardCounts();
  }, [loadDecksWithCardCounts]);

  const handleImportDeck = useCallback(async (filePathOrTrigger?: string) => {
    try {
      // Get the file service (platform-aware)
      const fileService = getFileService();
      const db = await getDatabaseService();

      // Import JSON data from file
      const data = await fileService.importJSON();

      // Import data into database
      const result = await db.importData(data);

      if (result.success) {
        // Refresh the deck list to show the imported deck
        await loadDecksWithCardCounts();

        // Show success message
        alert(`Import successful!\nDeck imported successfully with ID: ${result.deckId}`);

        // Optionally navigate to the imported deck
        if (result.deckId) {
          navigate(`/deck/${result.deckId}/edit`);
        }
      } else {
        throw new Error(result.error || 'Import failed');
      }

    } catch (error) {
      console.error('Import failed:', error);
      const errorMessage = `Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      alert(errorMessage);
    }
  }, [loadDecksWithCardCounts, navigate]);

  // Menu event handlers
  useEffect(() => {
    const unsubscribeNewDeck = window.electronAPI?.onMenuNewDeck(() => {
      setShowNewDeckModal(true);
    });

    const unsubscribeImportDeck = window.electronAPI?.onMenuImportDeck((filePath: string) => {
      handleImportDeck(filePath);
    });

    const unsubscribeExportDecks = window.electronAPI?.onMenuExportDecks(() => {
      setShowExportDecksModal(true);
    });

    return () => {
      unsubscribeNewDeck?.();
      unsubscribeImportDeck?.();
      unsubscribeExportDecks?.();
    };
  }, [handleImportDeck]);

  const filteredDecks = state.decks.filter(deck => {
    const matchesSearch = deck.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         deck.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDifficulty = !difficultyFilter || deck.difficulty === difficultyFilter;
    const matchesLanguage = !languageFilter || deck.targetLanguages.includes(languageFilter);

    return matchesSearch && matchesDifficulty && matchesLanguage;
  }).sort((a, b) => a.name.localeCompare(b.name));


  const handleDeleteDeck = (deck: Deck) => {
    setDeckToDelete(deck);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deckToDelete) {
      try {
        await deleteDeck(deckToDelete.id);
        setShowDeleteModal(false);
        setDeckToDelete(null);
        await loadDecksWithCardCounts();
      } catch (error) {
        console.error('Failed to delete deck:', error);
      }
    }
  };

  const handleResetProgress = (deck: Deck) => {
    setDeckToResetProgress(deck);
    setShowResetProgressModal(true);
  };

  const confirmResetProgress = async () => {
    if (deckToResetProgress) {
      try {
        const db = await getDatabaseService();
        const result = await db.clearDeckProgress(deckToResetProgress.id);
        if (result.success) {
          console.log(`Reset progress for deck: ${result.deletedRecords} records deleted`);
        } else {
          console.error('Failed to reset deck progress:', result.error);
          alert(`Failed to reset progress: ${result.error}`);
        }
        setShowResetProgressModal(false);
        setDeckToResetProgress(null);
      } catch (error) {
        console.error('Failed to reset deck progress:', error);
        alert('Failed to reset progress. Please try again.');
      }
    }
  };;

  const handleCreateDeck = async (deckData: Omit<Deck, 'id' | 'createdAt' | 'modifiedAt'>) => {
    try {
      const newDeck = await createDeck(deckData);
      setShowNewDeckModal(false);
      setShowAIDeckModal(false);
      navigate(`/deck/${newDeck.id}/edit`);
    } catch (error) {
      console.error('Failed to create deck:', error);
    }
  };

  const handleExportDecks = async (selectedDeckIds: string[]) => {
    try {
      // Get the file service (platform-aware)
      const fileService = getFileService();
      const db = await getDatabaseService();

      // Get export data from database
      const exportData = await db.exportSelectedDecks(selectedDeckIds);

      // Generate filename with current date
      const filename = `flash-e-decks-export-${new Date().toISOString().split('T')[0]}.json`;

      // Export to file (platform-aware)
      await fileService.exportJSON(exportData, filename);

      // Close modal and show success message
      setShowExportDecksModal(false);
      alert(`Export successful!\n${selectedDeckIds.length} deck(s) exported successfully.`);

    } catch (error) {
      console.error('Export failed:', error);
      const errorMessage = `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`;
      alert(errorMessage);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Easy': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900';
      case 'Medium': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900';
      case 'Hard': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900';
      default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const uniqueLanguages = Array.from(
    new Set(state.decks.flatMap(deck => deck.targetLanguages))
  ).sort();

  if (state.loading && state.decks.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex items-center space-x-2">
          <div className="spinner"></div>
          <span className="text-gray-600 dark:text-gray-400">Loading decks...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Flash Card Decks</h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Master programming concepts for technical interviews
              </p>
            </div>
            <button
              onClick={() => setShowNewDeckModal(true)}
              className="btn btn-primary"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Deck
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex-1 min-w-64 relative">
              <input
                type="text"
                placeholder="Search decks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pr-8"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  aria-label="Clear search"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            
            <select
              value={difficultyFilter}
              onChange={(e) => setDifficultyFilter(e.target.value)}
              className="form-select w-40"
            >
              <option value="">All Difficulties</option>
              <option value="Easy">Easy</option>
              <option value="Medium">Medium</option>
              <option value="Hard">Hard</option>
            </select>
            
            <select
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
              className="form-select w-40"
            >
              <option value="">All Languages</option>
              {uniqueLanguages.map(language => (
                <option key={language} value={language}>{language}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Decks Grid */}
        {filteredDecks.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No decks found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || difficultyFilter || languageFilter
                ? 'Try adjusting your search filters'
                : 'Get started by creating your first deck'
              }
            </p>
            {!searchTerm && !difficultyFilter && !languageFilter && (
              <div className="mt-6">
                <button
                  onClick={() => setShowNewDeckModal(true)}
                  className="btn btn-primary"
                >
                  Create New Deck
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDecks.map(deck => (
              <div key={deck.id} className="card hover:shadow-lg transition-shadow">
                <div className="card-body">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                      {deck.name}
                    </h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(deck.difficulty)}`}>
                      {deck.difficulty}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                    {deck.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-1 mb-4">
                    {deck.targetLanguages.map(language => (
                      <span
                        key={language}
                        className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded text-xs"
                      >
                        {language}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400 mb-4">
                    <span>Modified {formatDate(deck.modifiedAt)}</span>
                    <span>{(deck as DeckWithCardCount).cardCount || 0} cards</span>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Link
                      to={`/deck/${deck.id}/play`}
                      className="btn btn-primary flex-1 text-center"
                    >
                      Study
                    </Link>
                    <Link
                      to={`/deck/${deck.id}/edit`}
                      className="btn btn-secondary"
                      title="Edit deck"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </Link>
                    <button
                      onClick={() => handleResetProgress(deck)}
                      className="btn btn-secondary"
                      title="Reset progress"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteDeck(deck)}
                      className="btn btn-danger"
                      title="Delete deck"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* New Deck Modal */}
        {showNewDeckModal && (
          <NewDeckModal
            onClose={() => setShowNewDeckModal(false)}
            onCreate={handleCreateDeck}
            onUseAI={() => {
              setShowNewDeckModal(false);
              setShowAIDeckModal(true);
            }}
          />
        )}

        {/* AI Deck Creator Modal */}
        {showAIDeckModal && (
          <AIDeckCreator
            onClose={() => setShowAIDeckModal(false)}
            onCreate={handleCreateDeck}
          />
        )}

        {/* Export Decks Modal */}
        {showExportDecksModal && (
          <ExportDecksModal
            decks={state.decks}
            onClose={() => setShowExportDecksModal(false)}
            onExport={handleExportDecks}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && deckToDelete && (
          <ConfirmationModal
            title="Delete Deck"
            message={`Are you sure you want to delete "${deckToDelete.name}"? This action cannot be undone and will also delete all cards in this deck.`}
            confirmText="Delete"
            confirmButtonClass="btn-danger"
            onConfirm={confirmDelete}
            onCancel={() => {
              setShowDeleteModal(false);
              setDeckToDelete(null);
            }}
          />
        )}

        {/* Reset Progress Confirmation Modal */}
        {showResetProgressModal && deckToResetProgress && (
          <ConfirmationModal
            title="Reset Progress"
            message={`Are you sure you want to reset all progress for "${deckToResetProgress.name}"? This will clear all answer history and performance data for this deck. Cards and preferences will be preserved.`}
            confirmText="Reset Progress"
            confirmButtonClass="btn-danger"
            onConfirm={confirmResetProgress}
            onCancel={() => {
              setShowResetProgressModal(false);
              setDeckToResetProgress(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

const NewDeckModal: React.FC<{
  onClose: () => void;
  onCreate: (deck: Omit<Deck, 'id' | 'createdAt' | 'modifiedAt'>) => void;
  onUseAI: () => void;
}> = ({ onClose, onCreate, onUseAI }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [difficulty, setDifficulty] = useState<'Easy' | 'Medium' | 'Hard'>('Easy');
  const [targetLanguages, setTargetLanguages] = useState<string[]>(['JavaScript']);
  const [categoryTags, setCategoryTags] = useState<string[]>([]);

  const languages = ['Java', 'JavaScript', 'Python', 'C++', 'C#', 'Go', 'Rust', 'TypeScript'];
  const categories = ['Arrays', 'Strings', 'Loops', 'Conditionals', 'Functions', 'Objects', 'Math', 'Algorithms'];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onCreate({
        name: name.trim(),
        description: description.trim(),
        difficulty,
        targetLanguages,
        categoryTags,
      });
    }
  };

  const toggleLanguage = (language: string) => {
    setTargetLanguages(prev =>
      prev.includes(language)
        ? prev.filter(l => l !== language)
        : [...prev, language]
    );
  };

  const toggleCategory = (category: string) => {
    setCategoryTags(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Create New Deck</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Try AI-Assisted Deck Creation</h3>
                <p className="text-sm text-blue-700 dark:text-blue-300">Let AI help you create comprehensive flash card decks quickly</p>
              </div>
              <button
                onClick={onUseAI}
                className="btn btn-primary"
              >
                Use AI
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <label className="form-label">Deck Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="form-input"
                placeholder="Enter deck name"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="form-textarea"
                placeholder="Describe what this deck covers"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Difficulty</label>
              <select
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value as 'Easy' | 'Medium' | 'Hard')}
                className="form-select"
              >
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Target Languages</label>
              <div className="flex flex-wrap gap-2">
                {languages.map(language => (
                  <button
                    key={language}
                    type="button"
                    onClick={() => toggleLanguage(language)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      targetLanguages.includes(language)
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {language}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Categories (Optional)</label>
              <div className="flex flex-wrap gap-2">
                {categories.map(category => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => toggleCategory(category)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      categoryTags.includes(category)
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!name.trim()}
              >
                Create Deck
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DeckBrowser;